import { Box, VStack, HStack, Text, Button, IconButton, Grid, GridItem, useColorModeValue, useBreakpointValue } from '@chakra-ui/react';
import { FiArrowLeft, FiEye, FiUserPlus, FiThumbsUp, FiShare, FiGift, FiChevronDown, FiPlay } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';

interface AnalyticsMetric {
  icon: any;
  label: string;
  value: string | number;
  color: string;
}

interface ContentPerformance {
  id: string;
  title: string;
  thumbnail: string;
  duration: string;
  views: number;
  likes: number;
  shares: number;
  donations: number;
}

const CreatorAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.900', 'white');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const analyticsMetrics: AnalyticsMetric[] = [
    { icon: FiEye, label: 'Views', value: '3,250', color: 'blue.500' },
    { icon: FiUserPlus, label: 'Subscribers', value: '+71', color: 'green.500' },
    { icon: FiThumbsUp, label: 'Likes', value: '80', color: 'purple.500' },
    { icon: FiShare, label: 'Shares', value: '41', color: 'orange.500' },
    { icon: FiGift, label: 'Support Received', value: '$45', color: 'pink.500' }
  ];

  const lastContentPerformance: ContentPerformance = {
    id: '1',
    title: 'The Batman (2022)',
    thumbnail: 'https://images.unsplash.com/photo-1635805737707-575885ab0820?w=300&h=200&fit=crop',
    duration: '2h 56mins',
    views: 50,
    likes: 41,
    shares: 22,
    donations: 30
  };

  return (
    <Box bg={bgColor} minH="100vh" p={isMobile ? 4 : 8}>
      <VStack gap={6} align="stretch" maxW="1200px" mx="auto">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack gap={4}>
            <IconButton
              aria-label="Go back"
              icon={<IconWrapper icon={FiArrowLeft} size={20} />}
              variant="ghost"
              colorScheme="purple"
              onClick={() => navigate(-1)}
            />
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>
              Annalistic
            </Text>
          </HStack>
          
          <Button
            rightIcon={<IconWrapper icon={FiChevronDown} size={16} />}
            variant="outline"
            colorScheme="purple"
            size="sm"
          >
            Last 28 days
          </Button>
        </HStack>

        {/* Overview Section */}
        <VStack gap={4} align="stretch">
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            Overview
          </Text>
          
          <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }} gap={4}>
            {analyticsMetrics.map((metric, index) => (
              <GridItem key={index}>
                <Box
                  bg={cardBg}
                  p={4}
                  borderRadius="lg"
                  textAlign="center"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <VStack gap={2}>
                    <IconWrapper 
                      icon={metric.icon} 
                      size={24} 
                      color={metric.color}
                    />
                    <Text fontSize="sm" color="gray.600" fontWeight="medium">
                      {metric.label}
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color={textColor}>
                      {metric.value}
                    </Text>
                  </VStack>
                </Box>
              </GridItem>
            ))}
          </Grid>
        </VStack>

        {/* Last Content Performance */}
        <VStack gap={4} align="stretch">
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            Last Content Performance
          </Text>
          
          <Box
            bg={cardBg}
            p={4}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
          >
            <HStack gap={4} align="start">
              {/* Thumbnail */}
              <Box
                w="120px"
                h="80px"
                borderRadius="md"
                overflow="hidden"
                position="relative"
                flexShrink={0}
              >
                <CachedImage
                  src={lastContentPerformance.thumbnail}
                  alt={lastContentPerformance.title}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  fallbackType="movie"
                />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  bg="blackAlpha.600"
                  borderRadius="full"
                  p={2}
                >
                  <IconWrapper icon={FiPlay} size={16} color="white" />
                </Box>
              </Box>

              {/* Content Info */}
              <VStack align="start" gap={2} flex="1">
                <HStack justify="space-between" w="100%">
                  <VStack align="start" gap={1}>
                    <Text fontSize="md" fontWeight="bold" color={textColor}>
                      {lastContentPerformance.title}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {lastContentPerformance.duration}
                    </Text>
                  </VStack>
                  <IconWrapper icon={FiChevronDown} size={16} color="gray.500" />
                </HStack>

                {/* Performance Metrics */}
                <Grid templateColumns="repeat(4, 1fr)" gap={4} w="100%">
                  <VStack gap={1}>
                    <IconWrapper icon={FiEye} size={16} color="blue.500" />
                    <Text fontSize="sm" color="gray.600">Views</Text>
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>
                      {lastContentPerformance.views}
                    </Text>
                  </VStack>
                  
                  <VStack gap={1}>
                    <IconWrapper icon={FiThumbsUp} size={16} color="purple.500" />
                    <Text fontSize="sm" color="gray.600">Likes</Text>
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>
                      {lastContentPerformance.likes}
                    </Text>
                  </VStack>
                  
                  <VStack gap={1}>
                    <IconWrapper icon={FiShare} size={16} color="orange.500" />
                    <Text fontSize="sm" color="gray.600">Shares</Text>
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>
                      {lastContentPerformance.shares}
                    </Text>
                  </VStack>
                  
                  <VStack gap={1}>
                    <IconWrapper icon={FiGift} size={16} color="pink.500" />
                    <Text fontSize="sm" color="gray.600">Donation</Text>
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>
                      ${lastContentPerformance.donations}
                    </Text>
                  </VStack>
                </Grid>
              </VStack>
            </HStack>
          </Box>
        </VStack>

        {/* Top Videos Section */}
        <VStack gap={4} align="stretch">
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            Top Videos
          </Text>
          
          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            textAlign="center"
          >
            <Text color="gray.500">
              No top videos data available yet
            </Text>
          </Box>
        </VStack>
      </VStack>
    </Box>
  );
};

export default CreatorAnalytics;
